import {
    Box,
    Button,
    CircularProgress,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    Building2,
    Check,
    ChevronDown,
    Eye,
    IdCard,
    MapPin,
    Phone,
    User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { formatCNPJInput, isValidCNPJ } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import InputMasked from '../../component/input/InputMasked';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import MobileSupplierForm from '../../component/supplier/MobileSupplierForm';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import PageScaffold from '../../component/pageScaffold/PageScaffold';

/**
 * Seção colapsável do formulário — copiada 1:1 de `customers/Form.js`/
 * `products/Form.js` (DESIGN-SYSTEM.md §5.5 "Accordion insertion form" /
 * §6.2).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value, highlight }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
                {value || '—'}
            </Box>
        </Box>
    );
}

// Radio-card de tipo de fornecedor (Pessoa Jurídica roxo / Pessoa Física
// azul) — DESIGN-SYSTEM.md §5.5, mesma convenção de cor de
// `prototipo-detalhe-cliente.html`/`StatusChip domain="personType"`. Sem
// equivalente em `customers/Form.js` (que usa seg-tabs em vez de
// radio-cards) porque o item de Fornecedores pediu explicitamente esse
// padrão visual (`prototipo-lista-fornecedores.html`).
function TypeCard({ active, tone, icon: Icon, title, description, onClick }) {
    const theme = useTheme();
    const activeColor = tone === 'pj' ? theme.palette.secondary.main : theme.palette.primary.main;
    const activeBg = tone === 'pj' ? theme.palette.secondary.bg : theme.palette.info.bg;
    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                border: '1.5px solid',
                borderColor: active ? activeColor : 'divider',
                borderRadius: '14px',
                p: '14px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                backgroundColor: active ? activeBg : 'background.subtle',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': { borderColor: active ? activeColor : 'text.disabled' },
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: active ? activeColor : 'background.paper',
                    color: active ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: active ? activeColor : 'divider',
                    transition: 'background-color .15s, color .15s',
                }}
            >
                <Icon size={16} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.35 }}>{description}</Typography>
            </Box>
            {active && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: activeColor,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={10} strokeWidth={3} />
                </Box>
            )}
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell && id ? 'Editar fornecedor' : '');
    // Largura real do menu lateral — usada para alinhar o rodapé de ações fixo
    // (`.action-footer` do protótipo) com a borda do conteúdo. Ref:
    // `customers/Form.js`/`products/Form.js`.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    // Estado do autopreenchimento de endereço via CEP (ViaCEP real, através
    // de `GET /cep`, endpoint já existente) — 3 estados visuais pedidos pelo
    // item ("buscando"/"encontrado"/"não encontrado"), preenchidos a partir
    // de uma resposta real em vez de uma base fake (a base fake do protótipo
    // é uma simulação necessária só porque o protótipo é HTML estático sem
    // backend; aqui já existe `GET /cep` funcionando, reaproveitado 1:1 do
    // formulário anterior).
    const [cepStatus, setCepStatus] = useState('idle'); // idle | loading | found | notfound
    const cepTimerRef = useRef(null);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['main']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));

    const goBack = () => navigate('/suppliers');

    useEffect(() => () => clearTimeout(cepTimerRef.current), []);

    const validationSchema = Yup.object({
        person_type: Yup.string(),
        legal_name: Yup.string()
            .required('Este campo é obrigatório')
            .min(3, 'Deve ter pelo menos 3 caracteres'),
        trade_name: Yup.string().when('person_type', {
            is: 'PJ',
            then: (schema) => schema.required('Nome Fantasia é obrigatório'),
            otherwise: (schema) => schema.notRequired(),
        }),
        document: Yup.string()
            .required('Documento é obrigatório')
            .test('doc-format', 'Documento inválido', function testDoc(value) {
                if (!value) return false;
                const isPJ = this.parent.person_type === 'PJ';
                if (!isPJ) return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value);
                // `isValidCNPJ` já cobre formato (12 primeiros caracteres
                // letra OU dígito + 2 dígitos verificadores — CNPJ
                // alfanumérico, Nota Técnica 2026.001 da Receita Federal) e
                // o cálculo do DV numa única checagem.
                return isValidCNPJ(value);
            }),
        email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
        website: Yup.string().url('URL inválida'),
        cell_phone: Yup.string()
            .required('Celular é obrigatório')
            .matches(/^\(\d{2}\) \d{5}-\d{4}$/, 'Celular deve estar no formato (XX) XXXXX-XXXX'),
        // `excludeEmptyString: true` — mesmo ajuste do `zipcode` abaixo:
        // Telefone é opcional (só Celular é obrigatório), então um valor
        // vazio não pode falhar a regex do formato.
        phone_number: Yup.string()
            .matches(/^\(\d{2}\) \d{4}-\d{4}$/, { message: 'Telefone deve estar no formato (XX) XXXX-XXXX', excludeEmptyString: true }),
        // `.transform(...)` — sem isso, um "Número" em branco (string vazia)
        // é convertido para `NaN` pelo cast implícito de `Yup.number()` e
        // falha a validação de tipo, bloqueando o salvamento mesmo a seção
        // "Endereço" sendo opcional (mesmo bug latente, preexistente e não
        // corrigido aqui, no schema idêntico de `customers/Form.js`/no
        // formulário anterior de fornecedor).
        number_address: Yup.number()
            .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
            .positive('Número deve ser positivo')
            .integer('Número deve ser inteiro'),
        // `excludeEmptyString: true` — sem isso, um CEP em branco falha a
        // validação (regex não casa string vazia) e bloqueia o salvamento
        // mesmo a seção "Endereço" sendo opcional (mesmo bug latente,
        // preexistente e não corrigido aqui, no schema idêntico de
        // `customers/Form.js`/no formulário anterior de fornecedor).
        zipcode: Yup.string().matches(/^\d{5}-\d{3}$/, { message: 'CEP deve estar no formato 99999-999', excludeEmptyString: true }),
    });

    const handleShowSuccessSnackbar = (text) => {
        setOpenSnackbar(true);
        setMessage(text);
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        let text = 'Erro desconhecido';
        if (typeof responseData === 'string') {
            text = responseData;
        } else if (responseData && typeof responseData === 'object') {
            text = responseData.message || responseData.error || JSON.stringify(responseData);
        }
        setMessage(text);
        setSeverity('error');
    };

    const formik = useFormik({
        initialValues: {
            // `person_type` só controla rótulo/máscara/campos do formulário —
            // não existe coluna equivalente em `suppliers` (fv-api), então
            // nunca é enviado no payload. Em edição é inferido a partir da
            // quantidade de dígitos do documento (11 = CPF/PF, 14 = CNPJ/PJ).
            person_type: 'PJ',
            legal_name: '',
            trade_name: '',
            document: '',
            email: '',
            website: '',
            cell_phone: '',
            phone_number: '',
            address: '',
            complement: '',
            number_address: '',
            neighborhood: '',
            city: '',
            zipcode: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    legal_name: values.legal_name,
                    trade_name: values.person_type === 'PJ' ? values.trade_name : '',
                    document: values.document,
                    email: values.email,
                    website: values.website,
                    cell_phone: values.cell_phone,
                    phone_number: values.phone_number,
                    address: values.address,
                    complement: values.complement,
                    number_address: values.number_address ? parseInt(values.number_address, 10) : null,
                    neighborhood: values.neighborhood,
                    city: values.city,
                    zipcode: values.zipcode,
                };

                if (id) {
                    const response = await httpClient.put(`/suppliers/${id}`, payload);
                    if (response.status === 204) {
                        handleShowSuccessSnackbar('Fornecedor atualizado com sucesso!');
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                } else {
                    const response = await httpClient.post('/suppliers', payload);
                    if (response.status === 201) {
                        formik.resetForm();
                        setCepStatus('idle');
                        setOpenSections(['main']);
                        handleShowSuccessSnackbar('Fornecedor cadastrado com sucesso!');
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                }
            } catch (error) {
                console.error('Error saving supplier:', error);
                handleShowErrorSnackbar(error.response?.data || error.message);
            } finally {
                setLoading(false);
            }
        },
    });

    // Máscara manual do CNPJ (aceita letra OU dígito nos 12 primeiros
    // caracteres — CNPJ alfanumérico, Nota Técnica 2026.001 da Receita
    // Federal). Só usada quando `person_type === 'PJ'` — CPF continua com
    // `InputMasked`/`react-input-mask` (máscara 100% numérica). Mesma
    // técnica de `handleLicensePlateChange` (`pages/vehicles/Form.js`), o
    // outro campo do app que mistura letra e número.
    const handleDocumentChange = (event) => {
        formik.setFieldValue('document', formatCNPJInput(event.target.value));
    };

    const fetchSupplier = async () => {
        setLoadingData(true);
        try {
            const response = await httpClient.get(`/suppliers/${id}`);
            const data = response.data || {};
            const digits = (data.document || '').replace(/\D/g, '');
            formik.setValues({
                person_type: digits.length === 11 ? 'PF' : 'PJ',
                legal_name: data.legal_name || '',
                trade_name: data.trade_name || '',
                document: data.document || '',
                email: data.email || '',
                website: data.website || '',
                cell_phone: data.cell_phone || '',
                phone_number: data.phone_number || '',
                address: data.address || '',
                complement: data.complement || '',
                number_address: data.number_address || '',
                neighborhood: data.neighborhood || '',
                city: data.city || '',
                zipcode: data.zipcode || '',
            });
        } catch (error) {
            console.error('Error fetching supplier:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do fornecedor');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchSupplier();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const selectPersonType = (type) => {
        if (type === formik.values.person_type) return;
        formik.setFieldValue('person_type', type);
        if (type === 'PF') {
            formik.setFieldValue('trade_name', '');
        }
        // CPF e CNPJ são documentos de formato/comprimento diferentes — ao
        // trocar o tipo, limpa o documento em vez de tentar reformatar os
        // dígitos digitados sob a máscara antiga (`InputMasked`/
        // `react-input-mask` não lida bem com uma troca de `mask` externa
        // sobre um valor que já não corresponde ao template novo).
        formik.setFieldValue('document', '');
    };

    // Autopreenchimento de endereço a partir do CEP — dispara 550ms após o
    // usuário parar de digitar (mesmo delay do protótipo), usando o
    // endpoint real `GET /cep` (proxy do ViaCEP já existente em
    // `fv-api/config/router.go`). ViaCEP responde 200 com campos vazios
    // quando o CEP não existe — é esse o sinal usado para o estado
    // "não encontrado".
    const handleCepChange = (e) => {
        formik.handleChange(e);
        const rawValue = e.target.value || '';
        const digits = rawValue.replace(/\D/g, '');
        clearTimeout(cepTimerRef.current);
        if (digits.length < 8) {
            setCepStatus('idle');
            return;
        }
        setCepStatus('loading');
        cepTimerRef.current = setTimeout(async () => {
            try {
                const response = await httpClient.get(`/cep?cep=${digits}`);
                const data = response.data || {};
                if (data.logradouro || data.localidade || data.bairro) {
                    formik.setValues((prev) => ({
                        ...prev,
                        zipcode: rawValue,
                        address: data.logradouro || prev.address,
                        neighborhood: data.bairro || prev.neighborhood,
                        city: data.localidade || prev.city,
                    }));
                    setCepStatus('found');
                } else {
                    setCepStatus('notfound');
                }
            } catch (error) {
                console.error('Error fetching CEP:', error);
                setCepStatus('notfound');
            }
        }, 550);
    };

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    const personType = formik.values.person_type;
    const isPJ = personType === 'PJ';

    // Ref: `customers/Form.js`/`products/Form.js` `standaloneFieldSx`/
    // `standaloneInputBoxSx` — mesma casca visual de campo "ff-input" (52px,
    // radius 12px, fundo surface-2, label flutuante interno) aplicada aqui
    // verbatim.
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // Seções + campos obrigatórios por seção — o anel de progresso é
    // calculado a partir da fração de seções (com campos obrigatórios)
    // completas, mesmo algoritmo de `computeProgress()` em
    // `prototipo-lista-fornecedores.html` (não a fração de campos
    // individuais, diferente de `customers/Form.js`/`products/Form.js`).
    const isFieldFilled = (key) => Boolean(String(formik.values[key] ?? '').trim());
    const mainRequiredFields = isPJ ? ['legal_name', 'trade_name', 'document'] : ['legal_name', 'document'];
    const contactRequiredFields = ['cell_phone', 'email'];
    const isMainComplete = mainRequiredFields.every(isFieldFilled);
    const isContactComplete = contactRequiredFields.every(isFieldFilled);
    const requiredSectionsDone = [isMainComplete, isContactComplete].filter(Boolean).length;
    const progressPct = Math.round((requiredSectionsDone / 2) * 100);
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const sections = [
        {
            key: 'main',
            title: 'Tipo e identificação',
            subtitle: 'Pessoa Jurídica ou Física, razão social e documento',
            icon: IdCard,
            complete: isMainComplete,
        },
        {
            key: 'contact',
            title: 'Contato',
            subtitle: 'Telefone e e-mail para pedidos',
            icon: Phone,
            complete: isContactComplete,
        },
        {
            key: 'address',
            title: 'Endereço',
            subtitle: 'Opcional — usado para pedidos de compra e coleta',
            icon: MapPin,
            complete: false,
        },
    ];

    // Preview ao vivo — contact-card style (DESIGN-SYSTEM.md §5.5), mesmo
    // padrão de `customers/Form.js`.
    const getInitials = () => {
        const name = (formik.values.legal_name || '').trim();
        if (!name) return '?';
        return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
    };

    const previewName = formik.values.legal_name || 'Nome do fornecedor';
    const previewSub = isPJ
        ? (formik.values.trade_name || 'Sem nome fantasia informado')
        : 'Fornecedor Pessoa Física';
    const previewDocLabel = isPJ ? 'CNPJ' : 'CPF';
    const previewPhone = formik.values.cell_phone || formik.values.phone_number;

    if (loadingData) {
        return (
            <PageScaffold
                titlePageTarget="Fornecedores"
                links={[{ title: 'Fornecedores', link: '/suppliers' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                {isMobileShell ? (
                    <Stack spacing={1.25}>
                        {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                    </Stack>
                ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                    </Stack>
                    <Skeleton variant="rounded" height={420} sx={{ borderRadius: '20px' }} />
                </Box>
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Fornecedores"
            links={[{ title: 'Fornecedores', link: '/suppliers' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {isMobileShell ? (
                <MobileSupplierForm
                    formik={formik}
                    isPJ={isPJ}
                    onPersonTypeChange={selectPersonType}
                    onDocumentChange={handleDocumentChange}
                    onCepChange={handleCepChange}
                    cepStatus={cepStatus}
                    addressOpen={isSectionOpen('address')}
                    onToggleAddress={() => toggleSection('address')}
                    onCancel={goBack}
                    loading={loading}
                />
            ) : (
            <Box>
                {/* form-hero — título + descrição + anel de progresso, igual a `customers/Form.js`. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexWrap: 'wrap',
                        mb: 2.25,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            {id ? 'Editar fornecedor' : 'Novo fornecedor'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            Preencha por seções — o cartão ao lado mostra o resultado em tempo real.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 800,
                                }}
                            >
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                completo conforme você preenche
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* step-pills */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
                    {sections.map((sec, idx) => {
                        const isSecOpen = isSectionOpen(sec.key);
                        return (
                            <Box
                                key={sec.key}
                                onClick={() => ensureSectionOpen(sec.key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    px: '14px',
                                    py: '8px',
                                    borderRadius: '24px',
                                    border: '1.5px solid',
                                    borderColor: isSecOpen ? 'primary.main' : 'divider',
                                    backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                    color: isSecOpen ? 'primary.main' : 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: sec.complete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                        backgroundColor: sec.complete ? 'success.main' : 'transparent',
                                        color: sec.complete ? '#fff' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {sec.complete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                </Box>
                                {sec.title}
                            </Box>
                        );
                    })}
                </Box>

                {/* form-columns — acordeão + preview sticky */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                    <Stack spacing={1.75} sx={{ minWidth: 0 }} component="form" onSubmit={formik.handleSubmit}>
                        <AccordionSection
                            open={isSectionOpen('main')}
                            complete={sections[0].complete}
                            icon={sections[0].icon}
                            title={sections[0].title}
                            subtitle={sections[0].subtitle}
                            onToggle={() => toggleSection('main')}
                        >
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px' }}>
                                Tipo de fornecedor <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px', mb: 2.5 }}>
                                <TypeCard
                                    active={isPJ}
                                    tone="pj"
                                    icon={Building2}
                                    title="Pessoa Jurídica"
                                    description="Empresa com Razão Social e CNPJ."
                                    onClick={() => selectPersonType('PJ')}
                                />
                                <TypeCard
                                    active={!isPJ}
                                    tone="pf"
                                    icon={User}
                                    title="Pessoa Física"
                                    description="Fornecedor autônomo, identificado por CPF."
                                    onClick={() => selectPersonType('PF')}
                                />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        autoFocus
                                        label={requiredFieldLabel(isPJ ? 'Razão Social' : 'Nome completo')}
                                        fullWidth
                                        name="legal_name"
                                        value={formik.values.legal_name}
                                        onChange={formik.handleChange}
                                        error={formik.touched.legal_name && Boolean(formik.errors.legal_name)}
                                        helperText={formik.touched.legal_name && formik.errors.legal_name}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                                {isPJ && (
                                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                        <TextField
                                            label={requiredFieldLabel('Nome Fantasia')}
                                            fullWidth
                                            name="trade_name"
                                            value={formik.values.trade_name}
                                            onChange={formik.handleChange}
                                            error={formik.touched.trade_name && Boolean(formik.errors.trade_name)}
                                            helperText={formik.touched.trade_name && formik.errors.trade_name}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    {isPJ ? (
                                        // CNPJ (numérico ou alfanumérico — Nota Técnica 2026.001 da
                                        // Receita Federal): máscara manual em vez de
                                        // `InputMasked`/`react-input-mask`, igual a
                                        // `handleLicensePlateChange` (`pages/vehicles/Form.js`), o
                                        // outro campo do app que mistura letra e número.
                                        <TextField
                                            label={requiredFieldLabel(previewDocLabel)}
                                            fullWidth
                                            name="document"
                                            value={formik.values.document}
                                            onChange={handleDocumentChange}
                                            error={formik.touched.document && Boolean(formik.errors.document)}
                                            helperText={formik.touched.document && formik.errors.document}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    ) : (
                                        <InputMasked
                                            label={requiredFieldLabel(previewDocLabel)}
                                            fullWidth
                                            name="document"
                                            mask="999.999.999-99"
                                            value={formik.values.document}
                                            onChange={formik.handleChange}
                                            error={formik.touched.document && Boolean(formik.errors.document)}
                                            helperText={formik.touched.document && formik.errors.document}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('contact')}
                            complete={sections[1].complete}
                            icon={sections[1].icon}
                            title={sections[1].title}
                            subtitle={sections[1].subtitle}
                            onToggle={() => toggleSection('contact')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                <InputMasked
                                    label={requiredFieldLabel('Celular')}
                                    fullWidth
                                    name="cell_phone"
                                    mask="(99) 99999-9999"
                                    value={formik.values.cell_phone}
                                    onChange={formik.handleChange}
                                    error={formik.touched.cell_phone && Boolean(formik.errors.cell_phone)}
                                    helperText={formik.touched.cell_phone && formik.errors.cell_phone}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <InputMasked
                                    label="Telefone"
                                    fullWidth
                                    name="phone_number"
                                    mask="(99) 9999-9999"
                                    value={formik.values.phone_number}
                                    onChange={formik.handleChange}
                                    error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                                    helperText={formik.touched.phone_number && formik.errors.phone_number}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        label={requiredFieldLabel('E-mail')}
                                        fullWidth
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        label="Site"
                                        fullWidth
                                        name="website"
                                        value={formik.values.website}
                                        onChange={formik.handleChange}
                                        error={formik.touched.website && Boolean(formik.errors.website)}
                                        helperText={formik.touched.website && formik.errors.website}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('address')}
                            complete={sections[2].complete}
                            icon={sections[2].icon}
                            title={sections[2].title}
                            subtitle={sections[2].subtitle}
                            onToggle={() => toggleSection('address')}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: cepStatus === 'idle' ? 2 : 0.5, '& > *': { minWidth: 0 } }}>
                                <InputMasked
                                    label="CEP"
                                    fullWidth
                                    name="zipcode"
                                    mask="99999-999"
                                    value={formik.values.zipcode}
                                    onChange={handleCepChange}
                                    error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
                                    helperText={formik.touched.zipcode && formik.errors.zipcode}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                    <TextField
                                        label="Rua"
                                        fullWidth
                                        name="address"
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                        error={formik.touched.address && Boolean(formik.errors.address)}
                                        helperText={formik.touched.address && formik.errors.address}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>
                            {cepStatus !== 'idle' && (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    {cepStatus === 'loading' && (
                                        <>
                                            <CircularProgress size={13} />
                                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>Buscando endereço…</Typography>
                                        </>
                                    )}
                                    {cepStatus === 'found' && (
                                        <>
                                            <Check size={13} color={theme.palette.success.main} strokeWidth={3} />
                                            <Typography sx={{ fontSize: 11.5, color: 'success.main', fontWeight: 600 }}>
                                                Endereço encontrado automaticamente
                                            </Typography>
                                        </>
                                    )}
                                    {cepStatus === 'notfound' && (
                                        <Typography sx={{ fontSize: 11.5, color: 'warning.main', fontWeight: 600 }}>
                                            CEP não encontrado — preencha o endereço manualmente
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                <TextField
                                    label="Número"
                                    fullWidth
                                    name="number_address"
                                    value={formik.values.number_address}
                                    onChange={formik.handleChange}
                                    error={formik.touched.number_address && Boolean(formik.errors.number_address)}
                                    helperText={formik.touched.number_address && formik.errors.number_address}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Complemento"
                                    fullWidth
                                    name="complement"
                                    value={formik.values.complement}
                                    onChange={formik.handleChange}
                                    error={formik.touched.complement && Boolean(formik.errors.complement)}
                                    helperText={formik.touched.complement && formik.errors.complement}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Bairro"
                                    fullWidth
                                    name="neighborhood"
                                    value={formik.values.neighborhood}
                                    onChange={formik.handleChange}
                                    error={formik.touched.neighborhood && Boolean(formik.errors.neighborhood)}
                                    helperText={formik.touched.neighborhood && formik.errors.neighborhood}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                            <TextField
                                label="Cidade"
                                fullWidth
                                name="city"
                                value={formik.values.city}
                                onChange={formik.handleChange}
                                error={formik.touched.city && Boolean(formik.errors.city)}
                                helperText={formik.touched.city && formik.errors.city}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                        </AccordionSection>
                    </Stack>

                    {/* preview sticky — contact-card style (DESIGN-SYSTEM.md §5.5). */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                color: 'text.disabled',
                                mb: 1.25,
                            }}
                        >
                            <Eye size={13} /> Pré-visualização do fornecedor
                        </Typography>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: `${theme.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                boxShadow: theme.customShadows.md,
                                overflow: 'hidden',
                                mb: 1.75,
                            }}
                        >
                            <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '22px',
                                        backgroundColor: 'background.paper',
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 22,
                                        fontWeight: 800,
                                        color: 'primary.main',
                                        position: 'absolute',
                                        left: 20,
                                        bottom: -32,
                                        boxShadow: theme.customShadows.md,
                                    }}
                                >
                                    {getInitials()}
                                </Box>
                            </Box>
                            <Box sx={{ p: '42px 20px 20px' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                                    {previewName}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                                    {previewSub}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            px: '10px',
                                            py: '5px',
                                            borderRadius: '20px',
                                            backgroundColor: isPJ ? 'secondary.bg' : 'info.bg',
                                            color: isPJ ? 'secondary.main' : 'primary.main',
                                        }}
                                    >
                                        {isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'}
                                    </Box>
                                </Box>
                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
                                <PreviewRow label={previewDocLabel} value={formik.values.document} />
                                <PreviewRow label="Telefone" value={previewPhone} />
                                <PreviewRow label="E-mail" value={formik.values.email} />
                                <PreviewRow label="Cidade" value={formik.values.city} />
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios. Você pode editar tudo depois de salvar.
                        </Typography>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                {/* action-footer fixo — Cancelar/Salvar. */}
                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        Campos com * são obrigatórios
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={goBack}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={formik.handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : (id ? 'Atualizar fornecedor' : 'Salvar fornecedor')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
            )}
        </PageScaffold>
    );
}
